import { Camelized } from 'humps';
import { MerchantRestrictionPayload } from 'src/types/OrderRestrictions';

export enum SsoIdentifierTypes {
  EMAIL = 'EMAIL',
  INTERNAL_ID = 'INTERNAL_ID',
  OTHER = 'OTHER',
}
export interface PredefinedReason {
  name: string;
  reason: string;
}

export interface ExtraSegment {
  last_invoice_id: string;
  last_invoice_number: string;
  invoiced_entity_id?: string;
  invoiced_entity_name: string;
  invoiced_entity_ordinal: string;
}

export interface InvoiceMetadata {
  last_invoice_id: string;
  last_invoice_date: string;
  last_invoice_number: string;
  extra_segments: ExtraSegment[];
}

export interface GhPlusSubscription {
  id: string;
  corp_id: string;
  variation_id: string;
  start_date: string;
  end_date?: string;
  trial_length_days: number;
  monthly_fee: number;
  funding: string;
}

export type GhPlusSubscriptionResponseUI = Camelized<GhPlusSubscription>;

export interface GhPlusSubscriptionPayload {
  trial_length_days: number;
  monthly_fee: number;
  funding: string;
}

export type GhPlusSubscriptionPayloadUI = Camelized<GhPlusSubscriptionPayload>;

export enum InvoiceMethod {
  SINGLE = 'SINGLE',
  MULTI_LOCATION = 'MULTI_LOCATION',
  MULTI_DEPARTMENT = 'MULTI_DEPARTMENT',
}

export enum InvoicingSubtotalMethod {
  MEAL_TYPE_SUBTOTAL_INVOICE_METHOD = 'MEAL_TYPE',
  LOCATION_SUBTOTAL_INVOICE_METHOD = 'LOCATION',
  DEPARTMENT_SUBTOTAL_INVOICE_METHOD = 'DEPARTMENT',
}

export interface CreateCorpResponse {
  id: string;
  uri: string;
  already_exists: boolean;
}

export enum CorpSubscriptionFundingType {
  EMPLOYEE = 'EMPLOYEE',
  CORP = 'CORP',
}

export enum SSOIdentifierEnum {
  EMAIL = 'EMAIL',
  INTERNALID = 'INTERNAL_ID',
  OTHER = 'OTHER',
}

export interface CorpPayload {
  corp_id?: string;
  parent_id?: string;
  name: string;
  uri: string;
  allow_pickup_level: string;
  finance_fee_grace_days: number;
  finance_fee_annual_rate: number;
  min_start_date: string;
  select_firm_location_on_order: boolean;
  default_terms: number;
  effective_date: string;
  firm_transaction_fee: number;
  sales_person_code: number;
  legacy_firm_id?: string;
  phone_number: string;
  invoice_contact_first_name: string;
  invoice_contact_last_name: string;
  invoice_contact_phone_number: string;
  invoice_contact_email: string;
  invoice_contact_cc_emails: string[];
  invoice_contact_address1: string;
  invoice_contact_address2?: string;
  invoice_contact_city: string;
  invoice_contact_state: string;
  invoice_contact_zip: string;
  invoice_contact_country: string;
  intacct_term_name: string;
  invoice_rrule: string;
  invoice_time_zone: string;
  first_invoice_date: string;
  auto_invoicing_enabled: boolean;
  intacct_status: string;
  expense_codes_enabled: boolean;
  expense_codes_format_enabled: boolean;
  expense_codes_required: boolean;
  invoice_payment_method: string;
  max_credit_card_charge: number;
  expense_codes_format: string;
  predefined_reasons: PredefinedReason[];
  default_reason_name: string;
  predefined_reasons_enabled: boolean;
  predefined_reasons_required: boolean;
  brand: string;
  originating_brand: string;
  markets: string[];
  client_id: string;
  firm_designator?: string;
  contract_signed: string;
  allocations_reporting_enabled: boolean;
  departments_required: boolean;
  locations_required: boolean;
  invoicing_method: InvoiceMethod;
  invoicing_subtotal_method: InvoicingSubtotalMethod;
  allocations_permitted: boolean;
  sftp_expense_codes_enabled: boolean;
  sftp_employees_enabled: boolean;
  sftp_notification_emails: string[];
  sso_enabled: boolean;
  sso_provider_id: string;
  sso_identifier: string;
  sso_unique_identifier?: string;
  sso_redirect_enabled: boolean;
  sso_redirect_url: string | null;
  password_restricted: boolean;
  password_restricted_toggle_lock: boolean;
  automated_emails_disabled: boolean;
  group_order_enabled: boolean;
  order_history_enabled: boolean;
  order_policy_id?: string;
  ordering_policy_enabled?: boolean;
  file_encryption_enabled: boolean;
  gh_plus_enabled: boolean;
  gh_plus_subscription: GhPlusSubscriptionPayload;
  account_status?: string;
  pii_protected_marketing: boolean;
  corp_next_autopay?: string;
  corp_net_term_days?: string;
  corp_invoicing_period?: string;
  corp_next_invoice_start_date?: string;
  corp_next_invoice_end_date?: string;
  tax_exempt_enabled: boolean;
  order_cut_off_time?: number;
  default_merchant_restrictions: MerchantRestrictionPayload[];
  default_item_restrictions: string[];
  universal_accounts_enabled?: boolean;
}

export type CorpUIPayload = Camelized<CorpPayload>;

export interface ICorpResponse {
  id: string;
  corp_id?: string;
  parent_id?: string;
  name: string;
  uri: string;
  effective_date: string;
  firm_transaction_fee: number;
  allow_pickup_level: string;
  sales_person_code: number;
  max_locations_allowed: number;
  max_tags_allowed: number;
  seamless_firm_id?: string;
  legacy_firm_id?: string;
  phone_number: string;
  diner_location_required: boolean;
  diner_department_required: boolean;
  finance_fee_grace_days: number;
  finance_fee_annual_rate: number;
  invoice_contact_first_name: string;
  invoice_contact_last_name: string;
  invoice_contact_phone_number: string;
  invoice_contact_email: string;
  invoice_contact_cc_emails: string[];
  invoice_contact_address1: string;
  invoice_contact_address2?: string;
  invoice_contact_city: string;
  invoice_contact_state: string;
  invoice_contact_zip: string;
  invoice_contact_country: string;
  intacct_term_name: string;
  invoice_rrule: string;
  invoice_time_zone: string;
  last_invoice_id: string;
  last_invoice_number: string;
  first_invoice_date: string;
  auto_invoicing_enabled: boolean;
  intacct_status: string;
  expense_codes_enabled: boolean;
  expense_codes_format_enabled: boolean;
  expense_codes_required: boolean;
  expense_codes_exact_match_enabled: boolean;
  invoice_payment_method: string;
  min_start_date?: string;
  max_credit_card_charge: number;
  expense_codes_format: string;
  predefined_reasons: PredefinedReason[];
  default_reason_name: string;
  predefined_reasons_enabled: boolean;
  predefined_reasons_required: boolean;
  brand: string;
  originating_brand: string;
  markets: string[];
  industries: string[];
  client_id: string;
  firm_designator?: string;
  first_order_date: string;
  contract_signed: string;
  seamless_first_order_string: string;
  legacy_first_order_string: string;
  max_pickup_distance: number;
  order_cut_off_time: number;
  allocations_reporting_enabled: boolean;
  credit_card_autopay: string;
  departments_required: boolean;
  locations_required: boolean;
  invoicing_method: InvoiceMethod;
  invoicing_subtotal_method: InvoicingSubtotalMethod;
  invoice_metadata: InvoiceMetadata;
  expense_code_alias_singular: string;
  expense_code_alias_plural: string;
  allocations_permitted: boolean;
  sftp_expense_codes_enabled: boolean;
  sftp_invoices_enabled: boolean;
  sftp_employees_enabled: boolean;
  sftp_notification_emails: string[];
  enableSFTP: boolean;
  sso_enabled: boolean;
  sso_provider_id: string;
  sso_identifier: string;
  sso_unique_identifier?: string;
  sso_redirect_enabled: boolean;
  sso_redirect_url: string | null;
  password_restricted: boolean;
  password_restricted_toggle_lock: boolean;
  test_corp: boolean;
  automated_emails_disabled: boolean;
  group_order_enabled: boolean;
  order_history_enabled: boolean;
  cabs_diner_management_enabled: boolean;
  admin_ui_provider: string;
  sub_department_enabled: boolean;
  location_internal_id_enabled: boolean;
  ordering_policy_enabled?: boolean;
  order_policy_id?: string;
  file_encryption_enabled: boolean;
  gh_plus_enabled: boolean;
  gh_plus_subscription: GhPlusSubscription;
  sign_up_type: string;
  account_status?: string;
  pii_protected_marketing: boolean;
  corp_next_autopay?: string;
  corp_net_term_days?: string;
  corp_invoicing_period?: string;
  corp_next_invoice_start_date?: string;
  corp_next_invoice_end_date?: string;
  employee_count: number;
  invoice_producer: string;
  tax_exempt_enabled: boolean;
  default_merchant_restrictions: MerchantRestrictionPayload[];
  default_item_restrictions: string[];
  diner_notification_preferences: string[];
  select_firm_location_on_order?: boolean;
  default_terms?: number;
  universal_accounts_enabled?: boolean;
  external_id: string | null;
}

export type CorpUIResponse = Camelized<ICorpResponse>;

export type IParentCorpResponse = Pick<
  ICorpResponse,
  'name' | 'parent_id' | 'test_corp'
>;
